'use client';

/**
 * FlowTime — сводка дня и список задач рядом с таймером.
 *
 * Панель отвечает на два вопроса, которые возникают, пока идёт работа:
 * сколько сегодня сделано и что брать следующим. Всё остальное живёт
 * в журнале и отчётах.
 */

import { useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Check, Play, Plus, Trash2 } from 'lucide-react';
import type { FlowTimeApi } from '@/components/datahunter/use-flowtime';
import {
  dayKey,
  entrySeconds,
  fmtDur,
  fmtTime,
  focusSecondsOfDay,
  streak,
} from '@/lib/flowtime-format';

const GOAL_RING = 2 * Math.PI * 42;

export function FlowSidebar({ flow }: { flow: FlowTimeApi }) {
  const t = useTranslations('dh_flowtime_ui');
  const locale = useLocale();
  const [draft, setDraft] = useState('');
  const today = flow.todayKey;

  const stats = useMemo(() => {
    const focus = flow.data.entries.filter((e) => e.mode === 'focus' && dayKey(e.start) === today);
    const seconds = focusSecondsOfDay(flow.data.entries, today);
    const goal = flow.settings.goalHours * 3600;

    return {
      seconds,
      goal,
      // Доля цели: больше 100% кольцо не рисует, но текст показывает правду.
      ratio: goal > 0 ? Math.min(1, seconds / goal) : 0,
      percent: goal > 0 ? Math.round((seconds / goal) * 100) : 0,
      pomos: focus.filter((e) => e.completed).length,
      sessions: focus.length,
      streakDays: streak(flow.data.entries),
      // Доводимость: доля сессий, которые не были брошены на полпути.
      rate:
        focus.length > 0
          ? Math.round((focus.filter((e) => e.completed).length / focus.length) * 100)
          : null,
    };
  }, [flow.data.entries, flow.settings.goalHours, today]);

  const activeTasks = useMemo(
    () =>
      flow.data.tasks
        .filter((task) => !task.done)
        .sort((a, b) => a.order - b.order)
        .slice(0, 6),
    [flow.data.tasks],
  );

  const recent = useMemo(
    () =>
      flow.data.entries
        .filter((e) => e.mode === 'focus')
        .slice(-5)
        .reverse(),
    [flow.data.entries],
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const created = flow.addTask({ title: draft });
    if (created) setDraft('');
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Сводка дня */}
      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
        <h2 className="text-sm font-semibold mb-4">{t('today')}</h2>

        <div className="flex items-center gap-4 mb-4">
          <svg viewBox="0 0 100 100" className="w-20 h-20 -rotate-90 shrink-0" aria-hidden="true">
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke="var(--color-bg-elevated)"
              strokeWidth="9"
            />
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke="var(--color-text)"
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={GOAL_RING}
              strokeDashoffset={GOAL_RING * (1 - stats.ratio)}
              opacity={stats.ratio > 0.0005 ? 1 : 0}
              style={{ transition: 'stroke-dashoffset 700ms ease' }}
            />
          </svg>

          <div className="min-w-0">
            <div className="text-2xl font-semibold tabular-nums tracking-tight">
              {fmtDur(stats.seconds, locale)}
            </div>
            <p className="text-xs text-[var(--color-text-muted)]">
              {stats.goal > 0
                ? t('goal_of', { goal: fmtDur(stats.goal, locale), percent: stats.percent })
                : t('goal_unset')}
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-2">
          {[
            { label: t('stat_pomodoros'), value: String(stats.pomos) },
            { label: t('stat_sessions'), value: String(stats.sessions) },
            { label: t('stat_streak'), value: String(stats.streakDays) },
            {
              label: t('stat_rate'),
              value: stats.rate === null ? '—' : `${String(stats.rate)}%`,
            },
          ].map((s) => (
            <div key={s.label} className="rounded-lg bg-[var(--color-bg-elevated)] px-3 py-2">
              <dd className="text-lg font-semibold tabular-nums leading-tight">{s.value}</dd>
              <dt className="text-xs text-[var(--color-text-muted)]">{s.label}</dt>
            </div>
          ))}
        </dl>
      </section>

      {/* Задачи */}
      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
        <h2 className="text-sm font-semibold mb-3">{t('tasks')}</h2>

        <form onSubmit={submit} className="flex gap-2 mb-3">
          <input
            type="text"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            placeholder={t('task_placeholder')}
            maxLength={200}
            className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent-500)] transition-colors"
          />
          <button
            type="submit"
            aria-label={t('task_add')}
            className="w-9 h-9 grid place-items-center rounded-lg border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors shrink-0"
          >
            <Plus className="w-4 h-4" />
          </button>
        </form>

        {activeTasks.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)] py-2">{t('tasks_empty')}</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {activeTasks.map((task) => {
              const project = flow.projectById(task.projectId);
              const isCurrent = flow.session?.taskId === task.id;
              return (
                <li
                  key={task.id}
                  className={`group flex items-center gap-2.5 px-2 py-2 rounded-lg transition-colors ${
                    isCurrent
                      ? 'bg-[var(--color-bg-elevated)]'
                      : 'hover:bg-[var(--color-bg-elevated)]'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      flow.updateTask(task.id, { done: true });
                    }}
                    aria-label={t('task_done', { title: task.title })}
                    className="w-4 h-4 rounded border border-[var(--color-border)] hover:border-[var(--color-success-500)] grid place-items-center shrink-0 transition-colors"
                  >
                    <Check className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" />
                  </button>

                  {project && (
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: project.color }}
                      title={project.name}
                    />
                  )}

                  <span className="flex-1 text-sm truncate" title={task.title}>
                    {task.title}
                  </span>

                  <span className="text-xs text-[var(--color-text-muted)] tabular-nums shrink-0">
                    {flow.taskPomos(task.id)}
                    {task.estimate > 0 ? `/${String(task.estimate)}` : ''}
                  </span>

                  <button
                    type="button"
                    onClick={() => {
                      flow.setTask(task.id);
                      if (flow.mode !== 'focus') flow.setMode('focus');
                      if (!flow.running) flow.start();
                    }}
                    aria-label={t('task_work', { title: task.title })}
                    className="w-6 h-6 grid place-items-center rounded text-[var(--color-text-muted)] opacity-0 group-hover:opacity-100 hover:text-[var(--color-text)] transition-all shrink-0"
                  >
                    <Play className="w-3.5 h-3.5 fill-current" />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      flow.deleteTask(task.id);
                    }}
                    aria-label={t('task_delete', { title: task.title })}
                    className="w-6 h-6 grid place-items-center rounded text-[var(--color-text-muted)] opacity-0 group-hover:opacity-100 hover:text-[var(--color-danger-400)] transition-all shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Последние сессии */}
      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
        <h2 className="text-sm font-semibold mb-3">{t('recent_sessions')}</h2>
        {recent.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">{t('recent_empty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {recent.map((e) => {
              const task = flow.taskById(e.taskId);
              const project = flow.projectById(e.projectId);
              return (
                <li key={e.id} className="flex items-center gap-2.5 text-sm">
                  <span
                    className="w-0.5 h-6 rounded-full shrink-0"
                    style={{ background: project?.color ?? 'var(--color-text-muted)' }}
                  />
                  <span className="flex-1 truncate">{task?.title ?? (e.note || t('no_task'))}</span>
                  <span className="text-xs text-[var(--color-text-muted)] tabular-nums shrink-0">
                    {fmtTime(e.start)}
                  </span>
                  <span className="text-xs tabular-nums shrink-0 w-16 text-right">
                    {fmtDur(entrySeconds(e), locale)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
